import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { useGetCompaniesQuery, useGetTenantsCurrentCompanyQuery } from '@client/api';
import {
  Card,
  CardTitle,
  EmptyPlaceholder,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  useRouteDocumentTitle,
} from '@client/toolkit';

import { CompaniesToolbar, CompanyListRow } from './components';
import { useCompanyListSearchQueryParams, useCompanyListSortQueryParams } from './utils/use-company-list-query-params';

import { CompaniesPaths } from '../companies.router';
import { PageContentAnimation } from '../../../components/layout';

interface CompanyListRouteProps {
  role?: string;
}

export function CompanyListRoute({ role }: CompanyListRouteProps) {
  const { t } = useTranslation('companies');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  useRouteDocumentTitle(t('documentTitle'), CompaniesPaths.Companies);

  const [sortQueryParams] = useCompanyListSortQueryParams();
  const [searchQueryParams] = useCompanyListSearchQueryParams();

  const pageSize = 100;
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching } = useGetCompaniesQuery(
    {
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
      search: searchQueryParams.companiesSearch ? searchQueryParams.companiesSearch : undefined,
      'sort.Property': sortQueryParams.companiesSortProperty ? sortQueryParams.companiesSortProperty : undefined,
      'sort.Order': sortQueryParams.companiesSortOrder ? sortQueryParams.companiesSortOrder : undefined,
      role: role,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  // Get the current tenant's company to exclude it from the list
  const { data: tenantCompany } = useGetTenantsCurrentCompanyQuery();

  // Filter out the current tenant's company - it's managed via Profile
  const companies = useMemo(() => {
    const items = data?.items ?? [];
    if (!tenantCompany?.id) return items;
    return items.filter((company) => company.id !== tenantCompany.id);
  }, [data?.items, tenantCompany?.id]);
  const hasCompanyUsers = Array.isArray(companies) && companies.length > 0;
  const totalCount = data?.totalCount ?? 0;

  return (
    <>
      <PageContentAnimation>
        <Card className="mb-4 px-4" overflowX={false}>
          <CompaniesToolbar totalCount={totalCount} />
          <Card className="p-6 mb-16 bg-surface-base">
            <CardTitle variant="highlighted">{tGlobal('entity.company', { context: 'plural' })}</CardTitle>
            <Table className="border border-border rounded-md my-6">
              {!isLoading && !hasCompanyUsers && (
                <caption className="px-4 py-4 [caption-side:bottom]">
                  <EmptyPlaceholder>
                    {tCommon('noEntityFound', { entity: tGlobal('entity.company', { context: 'plural' }) })}
                  </EmptyPlaceholder>
                </caption>
              )}

              <TableHead>
                <TableHeadRow>
                  <TableHeadCell className="w-[25%]">{tGlobal('company.name')}</TableHeadCell>
                  <TableHeadCell className="w-[30%]">{t('table.address')}</TableHeadCell>
                  <TableHeadCell className="w-[20%]">{t('table.role')}</TableHeadCell>
                  <TableHeadCell />
                </TableHeadRow>
              </TableHead>
              <TableBody>
                {isLoading && Array.from(Array(10).keys()).map((x) => <CompanyListRow key={x} isFetching />)}

                {!isLoading &&
                  hasCompanyUsers &&
                  companies.map((company) => (
                    <CompanyListRow key={company.id} company={company} isFetching={isFetching} />
                  ))}
              </TableBody>
            </Table>
            <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
          </Card>
        </Card>
      </PageContentAnimation>
      <Outlet />
    </>
  );
}
